import React, { useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  BriefcaseBusiness,
  CalendarClock,
  Coins,
  Globe2,
  Languages,
  MapPin,
} from "lucide-react";
import ephLogo from "../../images/logo-min.png";
import ExpertPowerPointExperience from "./ExpertPowerPointExperience";
import ProposedRates from "./ProposedRates";

export const DIGITAL_PROFILE_SLIDE_STYLE = `
        /* ExistingDigitalPPT read-only mode: preserve the final-slide look
           while removing edit affordances and mutation feedback. */
        #pdp2-browser-slide.pdp2-readonly-mode input[readonly],
        #pdp2-browser-slide.pdp2-readonly-mode textarea[readonly],
        #pdp2-browser-slide.pdp2-readonly-mode select:disabled {
          cursor: text !important;
          opacity: 1 !important;
          color: inherit !important;
          -webkit-text-fill-color: currentColor !important;
        }

        #pdp2-browser-slide.pdp2-readonly-mode select:disabled {
          cursor: default !important;
        }

        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-profile-picture-section:focus,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-profile-picture-section:focus-within,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-identity-section:focus,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-identity-section:focus-within,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-position-section:focus,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-position-section:focus-within,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-left-field:focus,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-left-field:focus-within,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-main-section:focus,
        #pdp2-browser-slide.pdp2-readonly-mode .pdp2-main-section:focus-within {
          border-color: transparent !important;
        }

        #pdp2-browser-slide.pdp2-readonly-mode .ql-editor {
          cursor: text !important;
        }

        /* PreviewDigitalPPT-only ReactQuill styling.
           Every selector is scoped to this slide so it cannot affect Quill elsewhere. */
        #pdp2-browser-slide .pdp2-center-column {
          position: relative;
        }

        #pdp2-browser-slide .pdp2-summary-section {
          position: relative;
          overflow: visible !important;
        }

        #pdp2-browser-slide .pdp2-summary-editor {
          position: relative;
          flex: 1 1 auto;
          width: 100%;
          min-width: 0;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: visible;
          border: 0 !important;
        }

        #pdp2-browser-slide .pdp2-summary-editor .quill {
          position: static !important;
          flex: 1 1 auto !important;
          width: 100% !important;
          min-width: 0 !important;
          min-height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: visible !important;
          border: 0 !important;
        }

        /*
         * This is a custom Quill toolbar placed OUTSIDE the summary section.
         * It is anchored to the center column so the summary title/value area
         * stays completely separate from the editor controls.
         */
        #pdp2-browser-slide #pdp2-summary-toolbar.ql-toolbar.ql-snow {
          position: absolute !important;
          z-index: 70 !important;
          top: -34px !important;
          right: 0 !important;
          left: auto !important;
          bottom: auto !important;
          width: auto !important;
          min-width: 0 !important;
          min-height: 29px !important;
          margin: 0 !important;
          padding: 3px 5px !important;
          transform: translateY(3px) !important;
          float: none !important;
          display: flex !important;
          align-items: center !important;
          justify-content: flex-end !important;
          gap: 3px;
          border: 1px solid #d8e0e4 !important;
          border-radius: 9px !important;
          background: rgba(255, 255, 255, 0.97) !important;
          box-shadow: 0 5px 14px rgba(15, 23, 42, 0.10) !important;

          /* Same interaction principle as the section focus-border logic. */
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
          transition:
            opacity 140ms ease,
            transform 140ms ease,
            visibility 140ms ease !important;
        }

        /*
         * Show the toolbar only while the Summary section itself has focus,
         * or while focus is inside the toolbar after the user clicks one of
         * its formatting controls. Keeping the toolbar's own :focus-within
         * here prevents it from disappearing midway through a click.
         */
        #pdp2-browser-slide
          .pdp2-center-column:has(
            .pdp2-summary-section:focus,
            .pdp2-summary-section:focus-within,
            #pdp2-summary-toolbar:focus-within
          )
          #pdp2-summary-toolbar.ql-toolbar.ql-snow {
          opacity: 1 !important;
          visibility: visible !important;
          pointer-events: auto !important;
          transform: translateY(0) !important;
        }

        #pdp2-browser-slide
          #pdp2-summary-toolbar.ql-toolbar.ql-snow
          .ql-formats {
          margin: 0 2px 0 0 !important;
          display: inline-flex !important;
          align-items: center !important;
        }

        #pdp2-browser-slide
          #pdp2-summary-toolbar.ql-toolbar.ql-snow
          .ql-formats:last-child {
          margin-right: 0 !important;
        }

        #pdp2-browser-slide
          #pdp2-summary-toolbar.ql-toolbar.ql-snow
          button {
          width: 25px !important;
          height: 23px !important;
          padding: 3px !important;
        }

        #pdp2-browser-slide
          #pdp2-summary-toolbar.ql-toolbar.ql-snow
          .ql-picker.ql-size {
          width: 72px !important;
          height: 23px !important;
        }

        #pdp2-browser-slide
          #pdp2-summary-toolbar.ql-toolbar.ql-snow
          .ql-picker-label {
          height: 23px !important;
          padding: 2px 20px 2px 7px !important;
          display: flex !important;
          align-items: center !important;
          border-radius: 6px !important;
        }

        #pdp2-browser-slide
          #pdp2-summary-toolbar.ql-toolbar.ql-snow
          .ql-picker-options {
          border-radius: 8px !important;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14) !important;
        }

        /* Keep the text-size choices sensible for a single-slide profile. */
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor .ql-size-small {
          font-size: 12px !important;
        }

        #pdp2-browser-slide .pdp2-summary-editor .ql-editor .ql-size-large {
          font-size: 16px !important;
        }

        #pdp2-browser-slide .pdp2-summary-editor .ql-container.ql-snow {
          position: relative !important;
          flex: 1 1 auto !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          min-height: 0 !important;
          height: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: hidden !important;
          border: 0 !important;
          border-left: 0 !important;
          border-right: 0 !important;
          border-top: 0 !important;
          border-bottom: 0 !important;
          border-color: #ffffff !important;
          outline: 0 !important;
          box-shadow: none !important;
          font-family: Verdana, Arial, sans-serif;
        }

        #pdp2-browser-slide .pdp2-summary-editor .ql-editor {
          width: 100% !important;
          max-width: 100% !important;
          height: 100%;
          margin: 0 !important;
          padding: 5px 3px !important;
          overflow-y: auto;
          border: 0 !important;
          border-color: #ffffff !important;
          outline: 0 !important;
          box-shadow: none !important;
          scrollbar-width: none;
          -ms-overflow-style: none;
          color: #365460;
          font-family: Verdana, Arial, sans-serif;
          font-size: 14px;
          line-height: 1.35;
        }

        #pdp2-browser-slide .pdp2-summary-editor .quill,
        #pdp2-browser-slide .pdp2-summary-editor .ql-snow,
        #pdp2-browser-slide .pdp2-summary-editor .ql-container,
        #pdp2-browser-slide .pdp2-summary-editor .ql-container.ql-snow,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor {
          border-left-color: #ffffff !important;
          border-right-color: #ffffff !important;
          border-top-color: #ffffff !important;
          border-bottom-color: #ffffff !important;
          outline: 0 !important;
          box-shadow: none !important;
        }

        #pdp2-browser-slide .pdp2-summary-editor .ql-container.ql-snow {
          border: 0 !important;
          border-color: #ffffff !important;
        }

        #pdp2-browser-slide .pdp2-summary-editor .ql-editor::-webkit-scrollbar {
          width: 0 !important;
          height: 0 !important;
          display: none !important;
        }

        /*
         * Paragraph/list spacing rules:
         *
         * 1. Normal text blocks get the 8px system paragraph gap.
         * 2. A list that starts directly after a text block gets NO system gap.
         * 3. Explicit <br> tags remain user-controlled.
         * 4. When a new block starts after one or more top-level <br> tags,
         *    only the LAST <br> is visually consumed and replaced by the
         *    8px system gap. Earlier <br> tags remain as intentional empty lines.
         */
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > p,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > div,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ul,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ol {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }

        /* Normal paragraph-to-paragraph / list-to-paragraph spacing. */
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > div + div,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > div + p,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > p + div,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > p + p,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ul + div,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ul + p,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ol + div,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ol + p,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ul + ul,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ul + ol,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ol + ul,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ol + ol {
          margin-top: 8px !important;
        }

        /*
         * No div/p -> ul/ol selector on purpose:
         * a list starting directly below a paragraph gets no predefined gap.
         */

        /* Consume only the final top-level <br> immediately before a new block. */
        #pdp2-browser-slide
          .pdp2-summary-editor
          .ql-editor
          > br:has(+ div),
        #pdp2-browser-slide
          .pdp2-summary-editor
          .ql-editor
          > br:has(+ p),
        #pdp2-browser-slide
          .pdp2-summary-editor
          .ql-editor
          > br:has(+ ul),
        #pdp2-browser-slide
          .pdp2-summary-editor
          .ql-editor
          > br:has(+ ol) {
          display: none !important;
        }

        /* The following block receives the normal 8px paragraph gap. */
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > br + div,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > br + p,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > br + ul,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > br + ol {
          margin-top: 8px !important;
        }

        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ul,
        #pdp2-browser-slide .pdp2-summary-editor .ql-editor > ol {
          padding-left: 19px;
        }

        #pdp2-browser-slide .pdp2-summary-editor .ql-editor li {
          margin: 0 0 2px;
        }

        #pdp2-browser-slide .pdp2-summary-editor .ql-editor li:last-child {
          margin-bottom: 0;
        }

        @media print {
          #pdp2-browser-slide #pdp2-summary-toolbar {
            display: none !important;
          }

          #pdp2-browser-slide .pdp2-summary-editor .quill,
          #pdp2-browser-slide .pdp2-summary-editor .ql-container.ql-snow,
          #pdp2-browser-slide .pdp2-summary-editor .ql-editor {
            border: 0 !important;
            box-shadow: none !important;
          }

          #pdp2-browser-slide .pdp2-summary-editor .ql-editor {
            overflow: hidden !important;
          }
        }
`;

const DEFAULT_SECTION_VISIBILITY = {
  profilePicture: true,
  identity: true,
  position: true,
  location: true,
  region: true,
  language: true,
  totalExperience: true,
  availability: true,
  rate: true,
  summary: true,
  matching: true,
  experience: true,
  education: true,
};

function resolveDigitalProfileImage(value) {
  const imageValue = String(value || "").trim();
  if (!imageValue) return "";

  if (/^(data:|blob:|https?:\/\/)/i.test(imageValue)) {
    return imageValue;
  }

  return `https://images.expertpowerhouse.com${
    imageValue.startsWith("/") ? "" : "/"
  }${imageValue}`;
}

function polarToCartesian(cx, cy, radius, angleDeg) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function getPolygonPoints(cx, cy, radius, sides, rotation = 0) {
  return Array.from({ length: sides }, (_, i) => {
    const angle = rotation + (360 / sides) * i;
    return polarToCartesian(cx, cy, radius, angle);
  });
}

function pointsToString(points) {
  return points.map((p) => `${p.x},${p.y}`).join(" ");
}

const PDP2_RADAR_DEFAULT_LABELS = ["1", "2", "3", "4", "5"];
const PDP2_RADAR_DEFAULT_VALUES = [5, 4, 5, 3, 5];

export function RadarChart({
  values = PDP2_RADAR_DEFAULT_VALUES,
  labels = PDP2_RADAR_DEFAULT_LABELS,
  maxValue = 5,
  levels = 5,
  size = 850,
  renderSize = 420,
  valueBadge = "96%",
  strokeColor = "#365561",
  fillColor = "#365561",
  background = "none",
}) {
  const safeValues = values.map((v) =>
    Math.max(0, Math.min(Number(v) || 0, maxValue))
  );
  const sides = safeValues.length;
  const padding = size * 0.07;
  const cx = size / 2;
  const cy = size / 1.9;
  const radius = size / 2 - padding;
  const rotation = 0;

  const gridPolygons = Array.from({ length: levels }, (_, i) => {
    const r = (radius * (i + 1)) / levels;
    return getPolygonPoints(cx, cy, r, sides, rotation);
  });

  const axisPoints = getPolygonPoints(cx, cy, radius, sides, rotation);

  const dataPoints = safeValues.map((value, i) => {
    const r = (radius * value) / maxValue;
    const angle = rotation + (360 / sides) * i;
    return polarToCartesian(cx, cy, r, angle);
  });

  const fontSize = Math.max(10, Math.round(size * 0.07));

  return (
    <div
      className="pdp2-local-radar"
      style={{ backgroundColor: background }}
    >
      <svg
        viewBox={`0 0 ${size} ${size - 45}`}
        width={renderSize}
        height={(renderSize * (size - 45)) / size}
        className="pdp2-local-radar-svg"
        role="img"
        aria-label="Radar chart"
      >
        <g>
          {gridPolygons.map((polygon, i) => (
            <polygon
              key={`grid-${i}`}
              points={pointsToString(polygon)}
              fill="none"
              stroke="#6b7280"
              strokeWidth={i === gridPolygons.length - 1 ? 4 : 3}
              opacity={0.7}
            />
          ))}

          {axisPoints.map((point, i) => (
            <line
              key={`axis-${i}`}
              x1={cx}
              y1={cy}
              x2={point.x}
              y2={point.y}
              stroke="#6b7280"
              strokeWidth="5"
              opacity="0.5"
            />
          ))}

          {axisPoints.map((point, i) => {
            const dx = point.x - cx;
            const dy = point.y - cy;
            const distance = Math.sqrt(dx * dx + dy * dy) || 1;
            const labelGap = size * 0.055;
            const labelX = point.x + (dx / distance) * labelGap;
            const labelY = point.y + (dy / distance) * labelGap;
            const anchor =
              Math.abs(dx) < 1 ? "middle" : dx > 0 ? "start" : "end";

            return (
              <text
                key={`label-${i}`}
                x={labelX}
                y={labelY}
                fontSize={fontSize + 2}
                fill="#6b7280"
                textAnchor={anchor}
                dominantBaseline="middle"
              >
                {labels[i]}
              </text>
            );
          })}

          <polygon
            points={pointsToString(dataPoints)}
            fill={fillColor}
            fillOpacity="0.80"
            stroke={strokeColor}
            strokeWidth="5"
          />

          <circle
            cx={cx}
            cy={cy}
            r={106}
            fill={strokeColor}
            stroke="white"
            strokeWidth="10"
          />
          <text
            x={cx}
            y={cy + 15}
            textAnchor="middle"
            fontSize={Math.max(12, Math.round(size * 0.055))}
            fontWeight="700"
            fill="white"
          >
            {valueBadge}
          </text>
        </g>
      </svg>
    </div>
  );
}


export default function DigitalProfileSlide({
  data = {},
  readOnly = false,
  sectionVisibility: suppliedSectionVisibility,
  slideRef = null,
  renderHideButton = () => null,
  renderSaveState = () => null,
  profileImageNode = null,
  projectID,
  expertID,
  powerPointID,
  experienceData,
  summaryModules,
  summaryFormats,
  editing = {},
  onRateDimensions,
  showProposedRates = true,
  previewPrintLayout = false,
}) {
  // Bulk PDF must be rendered from the same DOM structure as the normal
  // editable PreviewDigitalPPT print snapshot. `readOnly` still describes the
  // data/usage intent, while `previewPrintLayout` deliberately keeps the
  // editable Preview DOM shape (Quill mode, form controls and Experience DOM)
  // so the browser computes identical flex geometry before we clone it.
  // MSbuttons removes all print-hidden/editing controls from the cloned deck.
  const isReadOnly = readOnly && !previewPrintLayout;
  const sectionVisibility = {
    ...DEFAULT_SECTION_VISIBILITY,
    ...(suppliedSectionVisibility || {}),
  };

  const proj_id = projectID;
  const exp_id = expertID;
  const identityMode =
    data.identityMode || (data.isNameShared === true ? "name" : "id");
  const projectType = data.projectType || "";
  const exProfilePicture = data.profilePicture ?? data.exProfilePicture ?? "";
  const exDigitalID = data.expertDigitalID ?? data.exDigitalID ?? "";
  const expertName = data.expertName ?? "";
  const exPosition = data.position ?? data.exPosition ?? "";
  const exLocation = data.location ?? data.exLocation ?? "";
  const region = data.region ?? "";
  const language = data.language ?? "";
  const exTotalExperience =
    data.totalExperience ?? data.exTotalExperience ?? "";
  const exAvailableComment =
    data.availableComment ?? data.Available_comment ?? data.exAvailableComment ?? "";
  const exProjectCurrency =
    data.projectCurrency ?? data.exProjectCurrency ?? "EUR";
  const exDailyRate = data.dailyRate ?? data.daily_rate ?? data.exDailyRate ?? "";
  const exBillingFrequency =
    data.billingFrequency ?? data.exBillingFrequency ?? "";
  const exFootNote = data.footNote ?? data.exFootNote ?? "no";
  const exSummary = data.summary ?? data.summ ?? data.exSummary ?? "";
  const exEducation = data.education ?? data.exEducation ?? "";

  const modules =
    summaryModules || { toolbar: { container: "#pdp2-summary-toolbar" } };
  const formats =
    summaryFormats || ["size", "bold", "italic", "underline", "list"];

  const renderPdp2HideButton = renderHideButton;
  const renderPdp2SaveState = renderSaveState;

  const setExDigitalID = editing.setDigitalID || (() => {});
  const saveExDigitalID = editing.saveDigitalID;
  const exDigitalIDLoading = editing.digitalIDLoading;
  const exDigitalIDSuccess = editing.digitalIDSuccess;
  const exDigitalIDError = editing.digitalIDError;

  const setExPosition = editing.setPosition || (() => {});
  const saveExPosition = editing.savePosition;
  const exPositionLoading = editing.positionLoading;
  const exPositionSuccess = editing.positionSuccess;
  const exPositionError = editing.positionError;

  const setExLocation = editing.setLocation || (() => {});
  const saveExLocation = editing.saveLocation;
  const exLocationLoading = editing.locationLoading;
  const exLocationSuccess = editing.locationSuccess;
  const exLocationError = editing.locationError;

  const setRegion = editing.setRegion || (() => {});
  const saveRegion = editing.saveRegion;
  const exRegionLoading = editing.regionLoading;
  const exRegionSuccess = editing.regionSuccess;
  const exRegionError = editing.regionError;

  const setLanguage = editing.setLanguage || (() => {});
  const saveLanguage = editing.saveLanguage;
  const exLanguageLoading = editing.languageLoading;
  const exLanguageSuccess = editing.languageSuccess;
  const exLanguageError = editing.languageError;

  const setExTotalExperience = editing.setTotalExperience || (() => {});
  const saveExTotalExperience = editing.saveTotalExperience;
  const exTotalExperienceLoading = editing.totalExperienceLoading;
  const exTotalExperienceSuccess = editing.totalExperienceSuccess;
  const exTotalExperienceError = editing.totalExperienceError;

  const setExAvailableComment = editing.setAvailableComment || (() => {});
  const saveExAvailableComment = editing.saveAvailableComment;
  const exAvailableCommentLoading = editing.availableCommentLoading;
  const exAvailableCommentSuccess = editing.availableCommentSuccess;
  const exAvailableCommentError = editing.availableCommentError;

  const setExProjectCurrency = editing.setProjectCurrency || (() => {});
  const saveExProjectCurrency = editing.saveProjectCurrency || (() => {});
  const setExDailyRate = editing.setDailyRate || (() => {});
  const saveExDailyRate = editing.saveDailyRate;
  const setExBillingFrequency = editing.setBillingFrequency || (() => {});
  const saveExBillingFrequency = editing.saveBillingFrequency;
  const setExFootNote = editing.setFootNote || (() => {});
  const saveExFootNote = editing.saveFootNote || (() => {});
  const exDailyRateLoading = editing.dailyRateLoading;
  const exDailyRateSuccess = editing.dailyRateSuccess;
  const exDailyRateError = editing.dailyRateError;

  const setExSummary = editing.setSummary || (() => {});
  const saveExSummary = editing.saveSummary;
  const removeFormatting = editing.removeFormatting;
  const exSummaryLoading = editing.summaryLoading;
  const exSummarySuccess = editing.summarySuccess;
  const exSummaryError = editing.summaryError;

  const setExEducation = editing.setEducation || (() => {});
  const saveExEducation = editing.saveEducation;
  const exEducationLoading = editing.educationLoading;
  const exEducationSuccess = editing.educationSuccess;
  const exEducationError = editing.educationError;

  const autoGrow = editing.autoGrow;

  const dimensionValues = data.dimensions || [
    data.dimension1,
    data.dimension2,
    data.dimension3,
    data.dimension4,
    data.dimension5,
  ];

  const pdp2Dimensions = dimensionValues.map((value, index) => ({
    value: value || "",
    setValue: (nextValue) => editing.setDimension?.(index, nextValue),
    save: () => editing.saveDimension?.(index),
    loading: editing.dimensionLoading?.[index],
    success: editing.dimensionSuccess?.[index],
    error: editing.dimensionError?.[index],
  }));

  const pdp2RadarValues = data.ratings || [
    Number(data.rating1) || 0,
    Number(data.rating2) || 0,
    Number(data.rating3) || 0,
    Number(data.rating4) || 0,
    Number(data.rating5) || 0,
  ];

  const pdp2MatchingPercentage = Math.round(
    (pdp2RadarValues.reduce((sum, value) => sum + value, 0) / 25) * 100
  );

  const setShowRateDimensionPopup = (show) => {
    if (show) onRateDimensions?.();
  };

  const currentYear = new Date().getFullYear();
  const pdp2EducationRef = useRef(null);

  const resizePdp2Education = () => {
    const element = pdp2EducationRef.current;
    if (!element || !sectionVisibility.education) return;

    if (!sectionVisibility.experience) {
      element.style.height = "100%";
      return;
    }

    element.style.height = "0px";
    const minHeight = 34;
    const maxHeight = 104;
    const nextHeight = Math.min(
      Math.max(element.scrollHeight, minHeight),
      maxHeight
    );
    element.style.height = `${nextHeight}px`;
  };

  useEffect(() => {
    resizePdp2Education();
  }, [exEducation, sectionVisibility.education, sectionVisibility.experience]);

  const displayedImage = resolveDigitalProfileImage(exProfilePicture);
  const staticProfileImage = (
    <div className="pdp2-profile-image-editor">
      <div className="pdp2-profile-image-frame">
        {displayedImage ? (
          <img
            className="pdp2-profile-image"
            src={displayedImage}
            alt="Consultant"
          />
        ) : (
          <div
            className="pdp2-profile-image-empty"
            aria-label="No profile picture"
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <style>{DIGITAL_PROFILE_SLIDE_STYLE}</style>
      <div
        ref={slideRef}
        id="pdp2-browser-slide"
        className={`pdp2-slide${isReadOnly ? " pdp2-readonly-mode" : ""}`}
      >
        <aside className="pdp2-left-rail">
          {sectionVisibility.profilePicture && (
            <section tabIndex={0} className="pdp2-profile-picture-section">
              {renderPdp2HideButton("profilePicture", "Profile picture")}
              <div className="pdp2-profile-picture-slot">
                {profileImageNode || staticProfileImage}
              </div>
            </section>
          )}

          {sectionVisibility.identity && identityMode && (
            <section tabIndex={0} className="pdp2-identity-section">
              {renderPdp2HideButton(
                "identity",
                identityMode === "name" ? "Name" : "EPH ID"
              )}

              {identityMode === "id" ? (
                <div className="pdp2-id-row">
                  <span className="pdp2-id-prefix">
                    {(projectType && projectType==="exp") ? "EPH_EXP_" : "EPH_CON_"}
                  </span>
                  <input
                    id="pdp2-expert-digital-id"
                    className="pdp2-id-input"
                    style={{
                      width: `${Math.min(
                        Math.max(String(exDigitalID || "").length, 2),
                        8
                      )}ch`,
                    }}
                    value={exDigitalID}
                    readOnly={isReadOnly}
                    onChange={
                      isReadOnly
                        ? undefined
                        : (e) => setExDigitalID(e.target.value)
                    }
                    onBlur={isReadOnly ? undefined : saveExDigitalID}
                  />
                  {renderPdp2SaveState(
                    exDigitalIDLoading,
                    exDigitalIDSuccess,
                    exDigitalIDError
                  )}
                </div>
              ) : (
                <div className="pdp2-identity-name-text">
                  {expertName || "Consultant"}
                </div>
              )}
            </section>
          )}

          {sectionVisibility.position && (
            <section tabIndex={0} className="pdp2-position-section">
              {renderPdp2HideButton("position", "Position")}
              <textarea
                className="pdp2-left-textarea pdp2-position-textarea"
                maxLength={160}
                rows={1}
                value={exPosition}
                readOnly={isReadOnly}
                onChange={
                  isReadOnly ? undefined : (e) => setExPosition(e.target.value)
                }
                onInput={autoGrow}
                onBlur={isReadOnly ? undefined : saveExPosition}
              />
              {renderPdp2SaveState(
                exPositionLoading,
                exPositionSuccess,
                exPositionError
              )}
            </section>
          )}

          <div className="pdp2-left-details">
            {sectionVisibility.location && (
              <section tabIndex={0} className="pdp2-left-field">
                {renderPdp2HideButton("location", "Location")}
                <MapPin className="pdp2-left-lucide" aria-hidden="true" />
                <textarea
                  className="pdp2-left-textarea"
                  maxLength={50}
                  rows={1}
                  value={exLocation}
                  readOnly={isReadOnly}
                  onChange={
                    isReadOnly ? undefined : (e) => setExLocation(e.target.value)
                  }
                  onInput={autoGrow}
                  onBlur={isReadOnly ? undefined : saveExLocation}
                />
                {renderPdp2SaveState(
                  exLocationLoading,
                  exLocationSuccess,
                  exLocationError
                )}
              </section>
            )}

            {sectionVisibility.region && (
              <section tabIndex={0} className="pdp2-left-field">
                {renderPdp2HideButton("region", "Region")}
                <Globe2 className="pdp2-left-lucide" aria-hidden="true" />
                <textarea
                  className="pdp2-left-textarea"
                  maxLength={50}
                  rows={1}
                  value={region || ""}
                  readOnly={isReadOnly}
                  onChange={
                    isReadOnly ? undefined : (e) => setRegion(e.target.value)
                  }
                  onInput={autoGrow}
                  onBlur={isReadOnly ? undefined : saveRegion}
                />
                {renderPdp2SaveState(
                  exRegionLoading,
                  exRegionSuccess,
                  exRegionError
                )}
              </section>
            )}

            {sectionVisibility.language && (
              <section tabIndex={0} className="pdp2-left-field">
                {renderPdp2HideButton("language", "Language")}
                <Languages className="pdp2-left-lucide" aria-hidden="true" />
                <textarea
                  className="pdp2-left-textarea"
                  maxLength={50}
                  rows={1}
                  value={language || ""}
                  readOnly={isReadOnly}
                  onChange={
                    isReadOnly ? undefined : (e) => setLanguage(e.target.value)
                  }
                  onInput={autoGrow}
                  onBlur={isReadOnly ? undefined : saveLanguage}
                />
                {renderPdp2SaveState(
                  exLanguageLoading,
                  exLanguageSuccess,
                  exLanguageError
                )}
              </section>
            )}

            {sectionVisibility.totalExperience && (
              <section tabIndex={0} className="pdp2-left-field pdp2-left-field-compact">
                {renderPdp2HideButton("totalExperience", "Total experience")}
                <BriefcaseBusiness className="pdp2-left-lucide" aria-hidden="true" />
                <div className="pdp2-inline-value-row">
                  <span className="pdp2-inline-label">Total experience:</span>
                  <input
                    className="pdp2-inline-input"
                    maxLength={10}
                    value={exTotalExperience}
                    readOnly={isReadOnly}
                    onChange={
                      isReadOnly
                        ? undefined
                        : (e) => setExTotalExperience(e.target.value)
                    }
                    onBlur={isReadOnly ? undefined : saveExTotalExperience}
                  />
                </div>
                {renderPdp2SaveState(
                  exTotalExperienceLoading,
                  exTotalExperienceSuccess,
                  exTotalExperienceError
                )}
              </section>
            )}

            {sectionVisibility.availability && (
              <section tabIndex={0} className="pdp2-left-field">
                {renderPdp2HideButton("availability", "Availability")}
                <CalendarClock className="pdp2-left-lucide" aria-hidden="true" />
                <textarea
                  className="pdp2-left-textarea"
                  maxLength={52}
                  rows={1}
                  value={exAvailableComment}
                  readOnly={isReadOnly}
                  onChange={
                    isReadOnly
                      ? undefined
                      : (e) => setExAvailableComment(e.target.value)
                  }
                  onInput={autoGrow}
                  onBlur={isReadOnly ? undefined : saveExAvailableComment}
                />
                {renderPdp2SaveState(
                  exAvailableCommentLoading,
                  exAvailableCommentSuccess,
                  exAvailableCommentError
                )}
              </section>
            )}

            {sectionVisibility.rate && (
              <section tabIndex={0} className="pdp2-left-field pdp2-rate-field">
                {renderPdp2HideButton("rate", "Rate")}
                <Coins className="pdp2-left-lucide" aria-hidden="true" />

                <div className="pdp2-rate-screen-row pdp2-screen-only">
                  <select
                    className="pdp2-rate-currency"
                    value={exProjectCurrency}
                    disabled={isReadOnly}
                    onChange={
                      isReadOnly
                        ? undefined
                        : (e) => {
                            setExProjectCurrency(e.target.value);
                            saveExProjectCurrency(e.target.value);
                          }
                    }
                  >
                    <option value="">---</option>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                    <option value="CHF">CHF</option>
                    <option value="AUD">AUD</option>
                    <option value="AED">AED</option>
                    <option value="INR">INR</option>
                  </select>
                  <input
                    className="pdp2-rate-value"
                    value={exDailyRate}
                    readOnly={isReadOnly}
                    onChange={
                      isReadOnly ? undefined : (e) => setExDailyRate(e.target.value)
                    }
                    onBlur={isReadOnly ? undefined : saveExDailyRate}
                  />
                  <input
                    className="pdp2-rate-frequency"
                    value={exBillingFrequency}
                    readOnly={isReadOnly}
                    onChange={
                      isReadOnly
                        ? undefined
                        : (e) => setExBillingFrequency(e.target.value)
                    }
                    onBlur={isReadOnly ? undefined : saveExBillingFrequency}
                  />
                  <span className="pdp2-rate-star">
                    {exFootNote === "yes" ? "*" : ""}
                  </span>
                  {showProposedRates ? (
                    <ProposedRates expert_id={exp_id} project_id={proj_id} />
                  ) : null}
                </div>

                <div className="pdp2-rate-print pdp2-print-only">
                  {`${exProjectCurrency || "EUR"} ${exDailyRate || ""} ${
                    exBillingFrequency || ""
                  }${exFootNote === "yes" ? "*" : ""}`
                    .replace(/\s+/g, " ")
                    .trim()}
                </div>

                {renderPdp2SaveState(
                  exDailyRateLoading,
                  exDailyRateSuccess,
                  exDailyRateError
                )}
              </section>
            )}
          </div>

          {sectionVisibility.rate && !isReadOnly && (
            <label className="pdp2-rate-footnote-bottom pdp2-screen-only">
              <input
                type="checkbox"
                checked={exFootNote === "yes"}
                onChange={(e) => {
                  const nextValue = e.target.checked ? "yes" : "no";
                  setExFootNote(nextValue);
                  saveExFootNote(nextValue);
                }}
              />
              <span>*rate includes EPH service</span>
            </label>
          )}

          {sectionVisibility.rate &&
            isReadOnly &&
            exFootNote === "yes" && (
              <span className="pdp2-rate-footnote-bottom pdp2-screen-only">
                *rate includes EPH service
              </span>
            )}

          {sectionVisibility.rate && exFootNote === "yes" && (
            <span className="pdp2-rate-footnote-bottom pdp2-rate-footnote-print pdp2-print-only">
              *rate includes EPH service
            </span>
          )}
        </aside>

        <header className="pdp2-slide-header">
          <img src={ephLogo} alt="expertpowerhouse" />
        </header>

        <main className="pdp2-content-grid">
          <div className="pdp2-center-column">
            {sectionVisibility.summary && (
              <>
                {!isReadOnly && (
                <div
                  id="pdp2-summary-toolbar"
                  className="ql-toolbar ql-snow pdp2-screen-only"
                  aria-label="Professional summary formatting"
                >
                  <span className="ql-formats">
                    <select
                      className="ql-size"
                      defaultValue=""
                      aria-label="Text size"
                    >
                      <option value="small">Small</option>
                      <option value="">Normal</option>
                      <option value="large">Large</option>
                    </select>
                  </span>

                  <span className="ql-formats">
                    <button
                      type="button"
                      className="ql-bold"
                      aria-label="Bold"
                    />
                    <button
                      type="button"
                      className="ql-italic"
                      aria-label="Italic"
                    />
                    <button
                      type="button"
                      className="ql-underline"
                      aria-label="Underline"
                    />
                  </span>

                  <span className="ql-formats">
                    <button
                      type="button"
                      className="ql-list"
                      value="ordered"
                      aria-label="Numbered list"
                    />
                    <button
                      type="button"
                      className="ql-list"
                      value="bullet"
                      aria-label="Bullet list"
                    />
                  </span>
                </div>
                )}

                <section tabIndex={0} className="pdp2-main-section pdp2-summary-section">
                <div className="pdp2-section-heading-row">
                  <h5>EPH Professional Summary</h5>
                  {renderPdp2HideButton("summary", "Professional Summary")}
                </div>

                <div className="pdp2-summary-editor">
                  <ReactQuill
                    theme="snow"
                    id="ppt-expertSummery"
                    modules={isReadOnly ? { toolbar: false } : modules}
                    formats={formats}
                    value={exSummary}
                    readOnly={isReadOnly}
                    onChange={
                      isReadOnly ? undefined : (e) => setExSummary(e)
                    }
                    onPaste={isReadOnly ? undefined : removeFormatting}
                    onBlur={isReadOnly ? undefined : saveExSummary}
                    style={{
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                    }}
                  />
                </div>

                {renderPdp2SaveState(
                  exSummaryLoading,
                  exSummarySuccess,
                  exSummaryError
                )}
                </section>
              </>
            )}

            {sectionVisibility.matching && (
              <section
                tabIndex={0}
                className="pdp2-main-section pdp2-matching-section"
                style={{
                  flex: sectionVisibility.summary ? "0 0 190px" : "1 1 auto",
                }}
              >
                <div className="pdp2-section-heading-row">
                  <h5>EPH Requirement Matching</h5>
                  {renderPdp2HideButton("matching", "Requirement Matching")}
                </div>

                <div className="pdp2-matching-body">
                  <div className="pdp2-dimension-list">
                    {pdp2Dimensions.map((dimensionItem, index) => (
                      <div className="pdp2-dimension-row" key={index}>
                        <span>{index + 1}:</span>
                        <input
                          value={dimensionItem.value || ""}
                          readOnly={isReadOnly}
                          onChange={
                            isReadOnly
                              ? undefined
                              : (e) => dimensionItem.setValue(e.target.value)
                          }
                          onBlur={isReadOnly ? undefined : dimensionItem.save}
                        />
                        {renderPdp2SaveState(
                          dimensionItem.loading,
                          dimensionItem.success,
                          dimensionItem.error
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="pdp2-radar-button"
                    disabled={isReadOnly}
                    onClick={
                      isReadOnly
                        ? undefined
                        : () => setShowRateDimensionPopup(true)
                    }
                    title={
                      isReadOnly
                        ? "Requirement matching"
                        : "Rate the dimensions"
                    }
                  >
                    <RadarChart
                      values={pdp2RadarValues}
                      labels={["1", "2", "3", "4", "5"]}
                      valueBadge={`${pdp2MatchingPercentage}%`}
                      size={850}
                      renderSize={155}
                    />
                  </button>
                </div>
              </section>
            )}
          </div>

          <div className="pdp2-right-column">
            {sectionVisibility.experience && (
              <section tabIndex={0} className="pdp2-main-section pdp2-experience-section">
                <div className="pdp2-section-heading-row">
                  <h5>Relevant Experience</h5>
                  {renderPdp2HideButton("experience", "Relevant Experience")}
                </div>
                <div className="pdp2-experience-body">
                  {powerPointID || Array.isArray(experienceData) ? (
                    <ExpertPowerPointExperience
                      expert_id={exp_id}
                      powerPointID={powerPointID}
                      readOnly={isReadOnly}
                      initialExperienceData={experienceData}
                    />
                  ) : null}
                </div>
              </section>
            )}

            {sectionVisibility.education && (
              <section
                tabIndex={0}
                className="pdp2-main-section pdp2-education-section"
                style={{
                  flex: sectionVisibility.experience ? "0 0 auto" : "1 1 auto",
                }}
              >
                <div className="pdp2-section-heading-row">
                  <h5>Education &amp; Certification</h5>
                  {renderPdp2HideButton("education", "Education & Certification")}
                </div>
                <textarea
                  ref={pdp2EducationRef}
                  id="ppt-expert-education"
                  className="pdp2-education-textarea"
                  value={exEducation}
                  readOnly={isReadOnly}
                  onChange={
                    isReadOnly ? undefined : (e) => setExEducation(e.target.value)
                  }
                  onInput={resizePdp2Education}
                  onBlur={isReadOnly ? undefined : saveExEducation}
                  style={{
                    flex: sectionVisibility.experience ? "0 0 auto" : "1 1 auto",
                  }}
                />
                {renderPdp2SaveState(
                  exEducationLoading,
                  exEducationSuccess,
                  exEducationError
                )}
              </section>
            )}
          </div>
        </main>

        <span className="pdp2-copyright">
          Copyright {currentYear} expertpowerhouse GmbH www.expertpowerhouse.com
        </span>
      </div>
    </>
  );
}
